/**
 * SecureEmbed Energy Portal - Tier Context
 */

import React, { createContext, useContext, useMemo } from 'react';
import { getTierLabel, TIER_ORDER } from '../constants/tiers';

interface TierContextType {
  userTier: number;
  tierLabel: string;
  hasAccess: (required: number) => boolean;
  isTasterMode: (required: number) => boolean;
}

const TierContext = createContext<TierContextType | undefined>(undefined);

export const TierProvider: React.FC<{ tier: number; children: React.ReactNode }> = ({ tier, children }) => {
  const value = useMemo(() => {
    const userIdx = TIER_ORDER.indexOf(tier);
    return {
      userTier: tier,
      tierLabel: getTierLabel(tier),
      hasAccess: (required: number) => tier >= required,
      isTasterMode: (required: number) => {
        if (tier >= required) return false;
        const reqIdx = TIER_ORDER.indexOf(required);
        return reqIdx >= 0 && userIdx >= 0 && reqIdx - userIdx === 1;
      },
    };
  }, [tier]);

  return <TierContext.Provider value={value}>{children}</TierContext.Provider>;
};

export const useTier = (): TierContextType => {
  const context = useContext(TierContext);
  if (!context) {
    throw new Error('useTier must be used within a TierProvider');
  }
  return context;
};
